// タップ辞書のルックアップ。PC版 src/dictionary.js の方式を移植するが、引き先は
// メモリ Map ではなく IndexedDB の 'dict' ストア（[dictId, 見出し小文字] → 訳文字列[]）。
// 有効な全辞書を横断して引き、辞書ごとに束ねて返す（複数辞書対応）。
import { db } from './db.js'
import { enabledDicts } from './dicts.js'

const MAX_PHRASE_WORDS = 6

// 不規則動詞：活用形→原形（PC版と同一テーブル）。
const IRREGULAR = (() => {
  const defs = {
    become: ['became'], begin: ['began', 'begun'], break: ['broke', 'broken'],
    bring: ['brought'], build: ['built'], buy: ['bought'], catch: ['caught'],
    choose: ['chose', 'chosen'], come: ['came'], deal: ['dealt'], do: ['did', 'done'],
    draw: ['drew', 'drawn'], drink: ['drank', 'drunk'], drive: ['drove', 'driven'],
    eat: ['ate', 'eaten'], fall: ['fell', 'fallen'], feel: ['felt'], fight: ['fought'],
    find: ['found'], fly: ['flew', 'flown'], forget: ['forgot', 'forgotten'],
    get: ['got', 'gotten'], give: ['gave', 'given'], go: ['went', 'gone'],
    grow: ['grew', 'grown'], have: ['had'], hear: ['heard'], hold: ['held'],
    keep: ['kept'], know: ['knew', 'known'], lay: ['laid'], lead: ['led'],
    leave: ['left'], lend: ['lent'], lie: ['lay', 'lain'], lose: ['lost'],
    make: ['made'], mean: ['meant'], meet: ['met'], pay: ['paid'],
    ride: ['rode', 'ridden'], ring: ['rang', 'rung'], rise: ['rose', 'risen'],
    run: ['ran'], say: ['said'], see: ['saw', 'seen'], sell: ['sold'],
    send: ['sent'], shake: ['shook', 'shaken'], shine: ['shone'], shoot: ['shot'],
    show: ['showed', 'shown'], sing: ['sang', 'sung'], sit: ['sat'], sleep: ['slept'],
    speak: ['spoke', 'spoken'], spend: ['spent'], stand: ['stood'],
    steal: ['stole', 'stolen'], stick: ['stuck'], strike: ['struck'],
    swim: ['swam', 'swum'], take: ['took', 'taken'], teach: ['taught'],
    tear: ['tore', 'torn'], tell: ['told'], think: ['thought'], throw: ['threw', 'thrown'],
    understand: ['understood'], wake: ['woke', 'woken'], wear: ['wore', 'worn'],
    win: ['won'], write: ['wrote', 'written'],
  }
  const m = new Map()
  for (const base in defs) for (const f of defs[base]) if (!m.has(f)) m.set(f, base)
  return m
})()

// 簡易な見出し語化（活用→基本形候補）。PC版と同一ロジック。
function lemmaCandidates(w) {
  const cands = [w]
  if (IRREGULAR.has(w)) cands.push(IRREGULAR.get(w))
  if (w.endsWith('ies') && w.length > 4) cands.push(w.slice(0, -3) + 'y')
  if (w.endsWith('es') && w.length > 3) cands.push(w.slice(0, -2))
  if (w.endsWith('s') && w.length > 2) cands.push(w.slice(0, -1))
  if (w.endsWith('ied') && w.length > 4) cands.push(w.slice(0, -3) + 'y')
  if (w.endsWith('ed') && w.length > 3) {
    cands.push(w.slice(0, -2))
    cands.push(w.slice(0, -1))
    if (w[w.length - 3] === w[w.length - 4]) cands.push(w.slice(0, -3))
  }
  if (w.endsWith('ing') && w.length > 4) {
    cands.push(w.slice(0, -3))
    cands.push(w.slice(0, -3) + 'e')
    if (w[w.length - 4] === w[w.length - 5]) cands.push(w.slice(0, -4))
  }
  if (w.endsWith('er') && w.length > 3) (cands.push(w.slice(0, -2)), cands.push(w.slice(0, -1)))
  if (w.endsWith('est') && w.length > 4) (cands.push(w.slice(0, -3)), cands.push(w.slice(0, -2)))
  return cands
}

const MID_PRONOUNS = new Set([
  'me', 'you', 'him', 'her', 'it', 'us', 'them',
  'my', 'your', 'his', 'its', 'our', 'their',
  'myself', 'yourself', 'himself', 'herself', 'itself',
  'ourselves', 'yourselves', 'themselves',
  'someone', 'something', 'somebody', 'oneself',
])

function uniq(arr) {
  return [...new Set(arr)]
}

// 複数キーを、有効な全辞書から横断取得（1トランザクション）。
// 戻り値 Map<key, { merged:[訳...], byDict:[{ id, name, entries[] }] }>。
async function getMany(keys) {
  const enabled = await enabledDicts()
  if (!enabled.length) return new Map()
  const d = await db()
  const tx = d.transaction('dict')
  const out = new Map()
  await Promise.all(
    keys.map(async (k) => {
      for (const dict of enabled) {
        const v = await tx.store.get([dict.id, k])
        if (v && v.length) {
          let rec = out.get(k)
          if (!rec) out.set(k, (rec = { merged: [], byDict: [] }))
          rec.byDict.push({ id: dict.id, name: dict.name, entries: v })
          for (const e of v) rec.merged.push(e)
        }
      }
    })
  )
  await tx.done
  return out
}

// 取込済みエントリ数（設定表示用）。レジストリの合計＝全件走査しない。
export async function dictCount() {
  const en = await enabledDicts()
  return en.reduce((s, d) => s + (d.count || 0), 0)
}

// 辞書が（有効なもので）取込済みか。レジストリだけで判定＝高速。
export async function dictReady() {
  const en = await enabledDicts()
  return en.some((d) => (d.count || 0) > 0)
}

// 1語を引く。戻り値 { word, found, base, entries[] }。
export async function lookup(word) {
  const w = String(word || '').trim().toLowerCase().replace(/^[^a-z']+|[^a-z']+$/g, '')
  if (!w) return { word, found: false, entries: [] }
  const cands = lemmaCandidates(w)
  const got = await getMany(uniq(cands))
  for (const c of cands) {
    if (got.has(c)) { const r = got.get(c); return { word, found: true, base: c, entries: r.merged, byDict: r.byDict } }
  }
  return { word, found: false, entries: [] }
}

// カーソル語からの前方テキストで「単語＋熟語」を前方累積で引く（Mouse Dictionary 方式）。
// 戻り値 { found, word, matches:[{ key, words, entries[] }] }（熟語優先・短い順）。
export async function lookupContext(rawText) {
  const tokens = String(rawText || '').toLowerCase().match(/[a-z][a-z'-]*/g) || []
  if (!tokens.length) return { found: false, matches: [] }

  const firstBases = uniq([tokens[0], ...lemmaCandidates(tokens[0])])
  const maxL = Math.min(tokens.length, MAX_PHRASE_WORDS)

  // 候補キーを全部集めて一括取得 → その後で組み立てる。
  const plans = [] // { L, candidates:[key...] }
  const allKeys = []
  for (let L = 1; L <= maxL; L++) {
    const rest = tokens.slice(1, L)
    const restVariants = uniq(
      [rest, rest.filter((w) => !MID_PRONOUNS.has(w))].map((a) => a.join(' '))
    ).map((s) => (s ? s.split(' ') : []))
    const candidates = []
    for (const fb of firstBases) {
      for (const rv of restVariants) {
        const key = [fb, ...rv].join(' ')
        candidates.push({ key, words: 1 + rv.length })
        allKeys.push(key)
      }
    }
    plans.push(candidates)
  }
  const got = await getMany(uniq(allKeys))

  const matches = []
  const usedKeys = new Set()
  for (const candidates of plans) {
    for (const c of candidates) {
      if (got.has(c.key) && !usedKeys.has(c.key)) {
        usedKeys.add(c.key)
        const r = got.get(c.key)
        matches.push({ key: c.key, words: c.words, entries: r.merged, byDict: r.byDict })
        break // この L では最初のヒットのみ（PC版と同じ）
      }
    }
  }
  matches.sort((a, b) => {
    const pa = a.words >= 2 ? 0 : 1
    const pb = b.words >= 2 ? 0 : 1
    return pa - pb || a.words - b.words
  })
  return { found: matches.length > 0, word: tokens[0], matches }
}
